<template>
    <article class="start-page">
        <transition :name="confirm ? 'right' : 'left'" mode="out-in">
            <StartConfirm v-if="confirm"
                          :request="request"
                          :payment="payment"
                          :is-processing="isProcessing"
                          :price="price"
                          @cancel="confirm = false"
                          @submit="onSubmit"/>
            <div v-else-if="!confirm">
                <FormHeader/>
                <ShowStep :current="1"/>
                <form @submit.prevent="onConfirm">
                    <SectionLabel>お客様情報</SectionLabel>
                    <section class="content">
                        <div class="form-group">
                            <FormLabel required>名前</FormLabel>
                            <div class="flex flex-row w-form-row max-w-full">
                                <div class="flex-1 pr-2">
                                    <FormInput type="text" class="w-full" autocomplete="family-name"
                                               placeholder="山田" v-model="request.name1"/>
                                    <ErrorText name="name1" :errors="errors"/>
                                </div>
                                <div class="flex-1 pl-2">
                                    <FormInput type="text" class="w-full" autocomplete="given-name"
                                               placeholder="太郎" v-model="request.name2"/>
                                    <ErrorText name="name2" :errors="errors"/>
                                </div>
                            </div>
                        </div>
                        <div class="form-group">
                            <FormLabel required>フリガナ</FormLabel>
                            <div class="flex flex-row w-form-row max-w-full">
                                <div class="flex-1 pr-2">
                                    <FormInput type="text" class="w-full" placeholder="ヤマダ" v-model="request.kana1"/>
                                    <ErrorText name="kana1" :errors="errors"/>
                                </div>
                                <div class="flex-1 pl-2">
                                    <FormInput type="text" class="w-full" placeholder="タロウ" v-model="request.kana2"/>
                                    <ErrorText name="kana2" :errors="errors"/>
                                </div>
                            </div>
                        </div>
                        <div class="form-group">
                            <FormLabel required>郵便番号</FormLabel>
                            <div class="flex flex-row flex-wrap w-form-row max-w-full">
                                <div class="w-full lg:w-auto lg:flex-1 lg:pr-2">
                                    <FormInput type="text" v-model="request.postcode" class="w-full"
                                               placeholder="000-0000"
                                               maxlength="8"
                                               autocomplete="postal-code" :half-width="true"/>
                                    <ErrorText name="postcode" :errors="errors"/>
                                </div>
                                <div class="lg:flex-1 lg:pl-2">
                                    <button type="button" class="search-postal"
                                            @click="onAddressSuggest">
                                        郵便番号検索
                                    </button>
                                </div>
                            </div>
                        </div>
                        <PrefPicker v-model="request.pref" :errors="errors"/>
                        <FormGroup name="city" label="市区町村" autocomplete="address-level2"
                                   placeholder="神戸市"
                                   v-model="request.city" :errors="errors" required/>
                        <FormGroup name="address" label="それ以降の住所" autocomplete="address-line1"
                                   placeholder="北区鳴子2丁目17-2"
                                   v-model="request.address" :errors="errors" required/>
                        <FormGroup name="bill" label="マンション・建物名" autocomplete="address-line2"
                                   v-model="request.bill" :errors="errors"/>
                        <FormGroup label="電話番号" name="tel" autocomplete="tel" half-width
                                   placeholder="09012345678" required
                                   input-class="lg:w-250p"
                                   help-text="※ハイフン（-）無しで入力してください"
                                   v-model="request.tel" :errors="errors"/>
                        <FormGroup label="メールアドレス" name="email" autocomplete="email"
                                   placeholder="info@example.com" required
                                   input-class="lg:w-410p"
                                   v-model="request.email" :errors="errors">
                            <template v-slot:helpText>
                                ※入力ミスや受信拒否設定をされている場合は、当協会からのメールが届きません。<br/>
                                ※申込後の自動返信メールが届かない場合は協会までお問い合わせください。<br/>
                                ※携帯メールアドレスをご使用の場合、届かない可能性があります。 <br/>
                                必ず@regist.jaca2021.or.jpからのメールが受信できるように設定してください。<br/>
                            </template>
                        </FormGroup>
                        <FormGroup label="メールアドレス(確認用)" name="email_confirmation"
                                   placeholder="info@example.com" required
                                   input-class="lg:w-410p"
                                   v-model="request.email_confirmation" :errors="errors"/>
                        <div class="form-group">
                            <FormLabel required>お支払い方法の選択</FormLabel>
                            <div class="flex-1">
                                <FormInput type="select" v-model="request.payment_type"
                                           :options="paymentTypes" class="lg:w-200p"/>
                                <ErrorText :errors="errors" name="payment_type"/>
                                <CreditForm
                                    v-if="request.payment_type === 'credit'"
                                    @onChangePayTimes="e => request.pay_times = e"
                                    :pay-times="request.pay_times"
                                    v-model="payment.credit" :errors="errors"/>
                                <div v-if="request.payment_type === 'later'" class="mt-2">
                                    <div class="p-2">
                                        <img src="/images/post_pay.jpg" alt="GMO後払い" class="block w-96 max-w-full">
                                        <p class="mt-2">
                                            ご利用にあたっては
                                            <a href="https://www.gmo-ps.com/customer/" target="_blank"
                                               rel="noreferrer" class="link-text inline-block">こちらのご案内および注意事項</a>
                                            をご確認ください。
                                        </p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </section>
                    <SectionLabel>申し込み詳細</SectionLabel>
                    <section class="content">
                        <ApplyDetailTable :price="price" :is-selected="!!request.payment_type"/>
                        <Agreement :errors="errors" v-model="request.agreement"/>
                        <p class="p-2">
                            <ErrorText :errors="errors" name="device_info"/>
                        </p>
                        <div class="form-group">
                            <button class="submit-button mx-auto" :disabled="isProcessing">申し込み内容の確認</button>
                        </div>
                    </section>
                </form>
            </div>
        </transition>
        <input type="hidden" id="fraudbuster" name="fraudbuster" ref="deviceInfo"/>
        <Loading :is-show="isProcessing"/>
    </article>
</template>

<script>
import SectionLabel from "../../parts/kyuseiki/SectionLabel";
import FormGroup from "../../parts/kyuseiki/FormGroup";
import FormLabel from "../../parts/kyuseiki/FormLabel";
import FormInput from "../../parts/app/FormInput";
import yubinbango from "../../mixins/yubinbango";
import multiPayment from "../../mixins/multiPayment";
import CardExpireInput from "../../parts/app/CardExpireInput";
import moment from "moment";
import StartConfirm from "../../parts/app/StartConfirm";
import ApplyDetailTable from "../../parts/app/ApplyDetailTable";
import {mapActions, mapState} from "vuex";
import ErrorText from "../../parts/ErrorText";
import SecurityCodeInformation from "../../parts/app/SecurityCodeInformation";
import BrandLogos from "../../parts/app/BrandLogos";
import IndustryForm from "../../parts/kyuseiki/IndustryForm";
import TriggerForm from "../../parts/kyuseiki/TriggerForm";
import FormHeader from "../../parts/kyuseiki/FormHeader";
import Agreement from "../../parts/app/Agreement";
import PrefPicker from "../../parts/kyuseiki/PrefPicker";
import Loading from "../../parts/kyuseiki/Loading";
import ShowStep from "../../parts/kyuseiki/ShowStep";
import CreditForm from "../../parts/kyuseiki/CreditForm";

export default {
    name: "Start",
    components: {
        CreditForm,
        ShowStep,
        Loading,
        PrefPicker,
        Agreement,
        FormHeader,
        TriggerForm,
        IndustryForm,
        BrandLogos,
        SecurityCodeInformation,
        ErrorText,
        ApplyDetailTable, StartConfirm, CardExpireInput, FormInput, FormLabel, FormGroup, SectionLabel
    },
    mixins: [yubinbango, multiPayment],
    head: {
        title: {
            inner: '急性期ケア専門士模擬試験申し込み',
            separator: '|',
            complement: '一般社団法人日本急性期ケア協会'
        },
        script: [
            {
                type: 'text/javascript',
                src: 'https://fraud-buster.appspot.com/js/fraudbuster.js'
            },
        ]
    },

    data() {
        return {
            request: {
                name1: '',
                name2: '',
                kana1: '',
                kana2: '',
                postcode: '',
                pref: null,
                city: '',
                address: '',
                bill: '',
                tel: '',
                email: '',
                email_confirmation: '',
                industry: '-',
                trigger: '-',
                payment_type: null,
                pay_times: 1,
                agreement: false,
            },
            payment: {
                credit: {
                    cardno: '',
                    expire: moment().format('YYYYMM'),
                    securitycode: '',
                    holdername: ''
                },
                creditResult: {
                    token: null,
                    toBeExpiredAt: null,
                    maskedCardNo: null,
                    isSecurityCodeSet: null,
                }
            },
            confirm: false,
            errors: {}
        }
    },

    computed: {
        paymentTypes() {
            return [
                {
                    value: 'credit',
                    label: 'クレジットカード'
                },
                {
                    value: 'later',
                    label: 'コンビニ後払い'
                }
            ]
        },

        payTimesList() {
            return [
                {
                    value: 1,
                    label: '1回'
                },
                {
                    value: 'bonus',
                    label: 'ボーナス払'
                }
            ]
        },

        isProcessing() {
            return this.$store.state.apply_validate.requestStatus === 'pending' ||
                this.$store.state.apply_exec.requestStatus === 'pending';
        },

        ...mapState('price_get', {
            price: 'data',
        })
    },

    watch: {
        'request.postcode': function (postcode, old) {
            if (!/^\d{0,3}-?\d{0,4}$/.test(postcode)) {
                this.$nextTick(() => {
                    this.request.postcode = old;
                });
            }
        },

        'request.tel': function (tel, old) {
            if (!/^\d*$/.test(tel)) {
                this.$nextTick(() => {
                    this.request.tel = old;
                });
            }
        },

        confirm: function () {
            window.scrollTo({
                top: 0,
            });
        },

        "$store.state.apply_validate.requestStatus": function (status) {
            if (status === 'success') {
                this.resetStatus();
                this.confirm = true;
            } else if (status === 'failed') {
                this.resetStatus();
                this.$toastr.error('入力をご確認ください。');
            }
        },

        "$store.state.apply_exec.requestStatus": function (status) {
            if (status === 'success') {
                const applicationId = this.$store.state.apply_exec.applicationId;
                this.resetExecStatus();
                this.$router.replace({
                    path: '/thank_you',
                    query: {
                        res: applicationId
                    }
                });
            } else if (status === 'failed') {
                this.resetExecStatus();
                this.confirm = false;
                this.$toastr.error('登録が行えませんでした。');
            }
        },

        "$store.state.apply_validate.errors": function (errors) {
            this.errors = errors;
        },

        "$store.state.apply_exec.errors": function (errors) {
            this.errors = errors;
            if (errors['payment_type']) {
                setTimeout(() => {
                    this.$scrollTo(`#error_payment_type`, {
                        offset: -80
                    });
                }, 500);
            }
        },

        "request.payment_type": async function (payment_type) {
            try {
                if (payment_type) {
                    await this.getPrice(payment_type);
                }
            } catch (e) {
                this.$toastr.error('価格を取得できませんでした。');
            }
        }
    },

    mounted() {
        this.clearErrors();

        this.$gtag.event('begin_checkout', {
            currency: 'JPY',
            value: 10000,
            items: [{
                id: 'SKU_12345',
                name: 'jacatest',
                price: 10000,
                quantity: 1,
            }]
        })
    },

    methods: {
        ...mapActions('apply_validate', [
            'execValidate',
            'resetStatus',
            'clearErrors',
        ]),
        ...mapActions('apply_exec', [
            'execApply',
            'resetExecStatus',
        ]),
        ...mapActions('price_get', [
            'getPrice',
        ]),

        async onAddressSuggest() {
            const result = await this.resolvePostCode(this.request.postcode);
            this.request = {
                ...this.request,
                pref: result['region'],
                city: result['locality'],
                address: `${result['street']} ${result['extended']}`
            };
        },

        async parseCard() {
            if (this.request.payment_type === 'credit') {
                try {
                    const result = await this.getCardToken(this.payment.credit);
                    this.payment.creditResult = result;
                    return result['token'];
                } catch (e) {
                    this.$toastr.error(e.message);
                    this.confirm = false;
                    return undefined;
                }
            } else {
                return undefined;
            }
        },

        async onConfirm() {
            await this.execValidate({
                ...this.request,
                card_token: await this.parseCard(),
                device_info: this.$refs.deviceInfo.value,
            });
        },

        async onSubmit() {
            await this.execApply({
                ...this.request,
                card_token: await this.parseCard(),
                device_info: this.$refs.deviceInfo.value,
            });
        },
    }
}
</script>

<style scoped>

</style>
